import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChangePasswordResponse } from '@src/app/core/models/change-password-response.model';
import { SystemParameter } from '@src/app/core/models/system-parameter.model';
import AES from 'crypto-js/aes';
import encUtf8 from 'crypto-js/enc-utf8';
import { Observable, Subject } from 'rxjs';
import { AppConfig } from 'sigv-library';
import { ApiResponse } from '../models/api-response.model';
import { ForgetAccountOTP } from '../models/forget-account-otp.model';
import { ForgetAccount } from '../models/forget-account.model';
import { ApiWebSubmissionService } from './api-web-submission.service';

@Injectable({
  providedIn: 'root',
})
export class PasswordService {
  private readonly baseUrl: string = '';
  private AESEncryptKey?: string;
  private AESEncryptIV?: string;

  constructor(
    private http: HttpClient,
    private apiWebSubmissionService: ApiWebSubmissionService,
    protected appConfig: AppConfig
  ) {
    this.baseUrl = appConfig.getApiUrl('webSubmission');
    this.initAESEncryptKey();
    this.initAESEncryptIV();
  }

  /**
   * @description 修改密碼
   * @param param account: 帳號, currentPassword: 目前密碼, newPassword: 新密碼
   */
  changePassword(param: {
    account: string;
    currentPassword: string;
    newPassword: string;
    recaptchaToken: string;
  }): Observable<ApiResponse<ChangePasswordResponse> | null> {
    const subject$ = new Subject<ApiResponse<ChangePasswordResponse> | null>();
    this.apiWebSubmissionService
      .doPost<ApiResponse<any>>('/Login/ChangeDwp', param)
      .subscribe({
        next: (response) => {
          subject$.next(response);
          subject$.complete();
        },
        error: (error) => {
          console.error('ChangePassword:', error);
          subject$.next(null);
          subject$.complete();
        },
      });
    return subject$;
  }

  /**
   * @description 發送忘記密碼email
   * @param userAccount 帳號
   */
  forgetPassword(
    userAccount: string,
    type: string
  ): Observable<{ status: boolean; msg?: string; isSMS?: boolean }> {
    const subject$ = new Subject<{
      status: boolean;
      msg?: string;
      isSMS?: boolean;
    }>();
    if (userAccount) {
      this.apiWebSubmissionService
        .doPost<ApiResponse<ForgetAccount>>('/Login/ForgetDwp', {
          account: userAccount,
          validateType: type,
        })
        .subscribe({
          next: (result) => {
            const { rtnCode, msg } = result;
            let isSuccess = false;
            let message = '';
            switch (rtnCode) {
              case 0:
                isSuccess = true;
                break;
              case 99:
                isSuccess = false;
                message = msg;
                break;
            }
            subject$.next({
              status: isSuccess,
              msg: message,
              isSMS: result.data?.isSMS,
            });
            subject$.complete();
          },
          error: (error) => {
            console.error(error);
            subject$.next({ status: false });
            subject$.complete();
          },
        });
    } else {
      subject$.next({ status: false });
      subject$.complete();
    }
    return subject$;
  }

  /**
   * @description 發送忘記密碼email
   * @param userAccount 帳號
   */
  checkAccount(
    userAccount: string,
    validateType: string
  ): Observable<{ status: boolean; msg?: string; isSMS?: boolean }> {
    const subject$ = new Subject<{
      status: boolean;
      msg?: string;
      isSMS?: boolean;
    }>();
    if (userAccount) {
      this.apiWebSubmissionService
        .doPost<ApiResponse<ForgetAccount>>('/Login/CheckAccount', {
          account: userAccount,
          validateType: validateType,
        })
        .subscribe({
          next: (result) => {
            const { rtnCode, msg } = result;
            let isSuccess = false;
            let message = '';
            switch (rtnCode) {
              case 0:
                isSuccess = true;
                break;
              case 99:
                isSuccess = false;
                message = msg;
                break;
            }
            subject$.next({
              status: isSuccess,
              msg: message,
              isSMS: result.data?.isSMS,
            });
            subject$.complete();
            
          },
          error: (error) => {
            console.error(error);
            subject$.next({ status: false });
            subject$.complete();
          },
        });
    } else {
      subject$.next({ status: false });
      subject$.complete();
    }
    return subject$;
  }

  /**
   * @description 發送忘記密碼email
   * @param userAccount 帳號
   */
  checkOTP(
    userAccount: string,
    userOTP: string
  ): Observable<{ status: boolean; msg?: string; token?: string; dealerCode?: string; }> {
    const subject$ = new Subject<{
      status: boolean;
      msg?: string;
      token?: string;
      dealerCode?: string;
    }>();
    if (userAccount) {
      this.apiWebSubmissionService
        .doPost<ApiResponse<ForgetAccountOTP>>('/Login/CheckOTP', {
          account: userAccount,
          otp: userOTP,
        })
        .subscribe({
          next: (result) => {
            const { rtnCode, msg } = result;
            let isSuccess = false;
            let message = '';
            switch (rtnCode) {
              case 0:
                isSuccess = true;
                break;
              case 99:
                isSuccess = false;
                message = msg;
                break;
            }
            subject$.next({
              status: isSuccess,
              msg: message,
              token: result.data?.token ?? '',
              dealerCode: result.data?.dealerCode ?? ''
            });
            subject$.complete();

          },
          error: (error) => {
            console.error(error);
            subject$.next({ status: false });
            subject$.complete();
          },
        });
    } else {
      subject$.next({ status: false });
      subject$.complete();
    }
    return subject$;
  }

  /**
   * @description 忘記密碼時的修改密碼
   * @param param account: 帳號, newPassword: 新密碼
   */
  changeForgotPassword(param: {
    account: string;
    newPassword: string;
    checkDetail: string;
    recaptchaToken: string;
  }): Observable<ApiResponse<ChangePasswordResponse> | null> {
    const subject$ = new Subject<ApiResponse<ChangePasswordResponse> | null>();
    this.apiWebSubmissionService
      .doPost<ApiResponse<ChangePasswordResponse>>('/Login/SetNewDwp', param)
      .subscribe({
        next: (response) => {
          subject$.next(response);
          subject$.complete();
        },
        error: (error) => {
          console.error('ChangePassword:', error);
          subject$.next(null);
          subject$.complete();
        },
      });
    return subject$;
  }

  /**
   * @description 忘記密碼作業前由 url 取得參數確認身分
   * @param param account: 帳戶, checkDetail: 檢查碼
   */
  checkIdentity(param: {
    account: string;
    checkDetail: string;
  }): Observable<any> {
    const subject$ = new Subject<boolean>();
    this.apiWebSubmissionService
      .doPost<ApiResponse<any>>('/Login/CheckIdentity', param)
      .subscribe({
        next: (response) => {
          let isSuccess = false;
          if (response && response.rtnCode === 0) {
            isSuccess = true;
          }
          subject$.next(isSuccess);
          subject$.complete();
        },
        error: (error) => {
          console.error('checkIdentity', error);
          subject$.next(false);
          subject$.complete();
        },
      });
    return subject$;
  }

  getSystemParameter(
    typeCode: string
  ): Observable<ApiResponse<SystemParameter[]>> {
    return this.apiWebSubmissionService.doGet(
      `/Common/GetSystemParameter/${typeCode}`
    );
  }

  getEncryptedData(str: string): string {
    const key = encUtf8.parse(this.AESEncryptKey!);
    const iv = encUtf8.parse(this.AESEncryptIV!);
    const encryptedPassword = AES.encrypt(str, key ?? '', {
      iv,
    }).toString();

    return encryptedPassword;
  }

  private initAESEncryptKey(): void {
    this.http
      .get<any>(`${this.baseUrl}/Common/GetSystemParameter/AAD.DesKey`)
      .subscribe({
        next: (resp) => {
          if (resp.rtnCode !== 0) {
            throw resp.msg;
          }
          this.AESEncryptKey = resp.data[0].value;
        },
      });
  }

  private initAESEncryptIV(): void {
    this.http
      .get<any>(`${this.baseUrl}/Common/GetSystemParameter/AAD.DesIV`)
      .subscribe({
        next: (resp) => {
          if (resp.rtnCode !== 0) {
            throw resp.msg;
          }
          this.AESEncryptIV = resp.data[0].value;
        },
      });
  }
}
